import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Modern Day Missions vs Devotion Fund: Fiscal Sponsor or Direct Support Platform?';
const DESCRIPTION =
    "An honest comparison from a competing founder: Modern Day Missions' 501(c)(3) fiscal sponsorship and tax-deductible giving vs Devotion Fund's direct, recurring-first support platform — and why the right choice depends on your donor base, not the fee gap.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover12.jpg',
            datePublished: '2026-07-23',
            dateModified: '2026-07-23',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/modern-day-missions-vs-devotion-fund',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is giving through Modern Day Missions tax-deductible?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. Modern Day Missions is a 501(c)(3) fiscal sponsor, so gifts routed through it qualify for a U.S. tax deduction and the missionary receives an official donation receipt on the organization's behalf. Devotion Fund does not offer this today — donations are personal support gifts, not tax-deductible contributions.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is Modern Day Missions or Devotion Fund cheaper?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "On raw fees, Modern Day Missions is slightly cheaper — roughly 4.28% effective all-in at a typical $50/month gift versus Devotion Fund's 5% (bank transfer) or 7% (card). The gap is small enough that it rarely decides the choice on its own; tax deductibility, fund-usage rules, and supporter tooling matter more.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I use Modern Day Missions and Devotion Fund at the same time?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Some missionaries do run both — Modern Day Missions for donors who need a tax receipt, and Devotion Fund for the recurring relationship, updates, and donor CRM. It adds a second system to manage, but it lets you serve both donor types without asking either to compromise.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Who should choose Modern Day Missions over Devotion Fund?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "U.S.-based missionaries whose donor base skews toward itemizing, deduction-motivated givers, and who are comfortable with 501(c)(3) governance over how funds are used and released. If your supporters mostly take the standard deduction, or you serve outside the U.S. fiscal-sponsorship framework, the deduction matters less than a direct, self-serve platform.",
                    },
                },
            ],
        },
    ],
};

export default function ModernDayMissionsVsDevotionFund() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/modern-day-missions-vs-devotion-fund' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-mdm-vs-df-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover12.jpg" alt="A white cross against a clear blue sky" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Of all the platforms missionaries ask me about, Modern Day Missions is the one where the honest answer is "it depends on your donors, not on which platform is better." I run a competing platform, so weigh what follows accordingly. But this isn't really a fee comparison — it's a comparison between two different categories: a 501(c)(3) fiscal sponsor and a direct support platform.</p>

                    <h2>The short version</h2>

                    <p>Modern Day Missions is a <strong>fiscal sponsor</strong> — a U.S. 501(c)(3) nonprofit that takes in donations on a missionary's behalf, issues the donor a tax-deductible receipt, and releases funds to the missionary under the organization's governance. Devotion Fund is a <strong>direct support platform</strong> — donations go straight to the missionary's own bank account, with no intermediary holding or releasing funds, and no tax deduction attached.</p>

                    <p>Neither model is universally better. The right one depends on whether your donor base needs a deduction and whether you're comfortable trading direct control of funds for that benefit.</p>

                    <h2>What each platform actually costs</h2>

                    <p>Modern Day Missions' headline fee is <strong>6%</strong>, with processing included — but because of how it structures gift processing, the realistic all-in rate on a typical $50/month gift works out to roughly <strong>4.28% effective</strong>, per our <a href="/resources/true-cost-missionary-fundraising-platforms-2026">full seven-platform cost comparison</a>. Devotion Fund charges <strong>5% all-in on bank transfers, 7% all-in on card</strong>, processing absorbed either way, no exceptions.</p>

                    <p>That puts Modern Day Missions modestly cheaper on raw fees — under a percentage point, at typical gift sizes. It's a real gap, but it's small enough that it rarely settles the decision by itself. The bigger differences are structural.</p>

                    <h2>The deduction: the actual reason most missionaries consider Modern Day Missions</h2>

                    <p>This is the feature that matters. Because Modern Day Missions is a 501(c)(3), every gift through it is <strong>tax-deductible</strong> for the donor. For a support base of itemizing, higher-capacity donors, that deduction can be worth more to them than any fee difference — and it can be the deciding factor in whether they give at all, or how much.</p>

                    <p>Devotion Fund doesn't offer this today. Gifts through Devotion Fund are personal support — generous, but not deductible. For donors giving $25–$100/month who take the standard deduction (most donors, in practice), this rarely changes their decision. For a donor writing a larger check who itemizes, it can matter a great deal.</p>

                    <p>The honest question to ask yourself: <em>what fraction of my support base actually itemizes and cares about the deduction?</em> If it's a meaningful slice, that alone can outweigh everything else in this comparison.</p>

                    <h2>What fiscal sponsorship costs you in control and flexibility</h2>

                    <p>A 501(c)(3) fiscal sponsor doesn't just process the gift — it governs it. Funds are legally the organization's until released to you, which means:</p>
                    <ul>
                        <li><strong>Usage restrictions.</strong> How and when funds can be used is shaped by 501(c)(3) rules and the sponsoring organization's own policies, not solely by you.</li>
                        <li><strong>An application and approval process.</strong> Fiscal sponsorship generally isn't instant self-serve — you apply, get accepted, and operate within the organization's framework, unlike setting up a Devotion Fund page in about ten minutes.</li>
                        <li><strong>U.S.-only reach.</strong> Modern Day Missions' fiscal-sponsorship structure is built around the U.S. tax code, so it doesn't fit missionaries operating outside that framework, including many international and restricted-access workers.</li>
                    </ul>

                    <p>Devotion Fund's trade is the mirror image: funds go directly to your own bank account with no organizational approval or governance layer, but without the deduction that comes from that same governance.</p>

                    <h2>Where Devotion Fund is stronger: the ongoing supporter relationship</h2>

                    <p>Modern Day Missions is built to be a fiscal sponsor, not a creator platform — there's no native recurring-supporter dashboard, donor CRM, or newsletter tooling bundled in. Devotion Fund is built specifically around the ongoing relationship: a donor CRM that shows who's engaged and who's drifting, a built-in AI-assisted prayer-letter draft tool, and a recurring-by-default giving flow designed for monthly partnership rather than one-off deductible gifts. (See <a href="/resources/how-to-write-a-prayer-letter-people-actually-read">how to write a prayer letter people actually read</a> for the method, regardless of platform.)</p>

                    <p>Support usually lapses because a supporter stops hearing from the missionary, not because of a fee or a missing deduction — so if retention tooling is your bottleneck, that's a real point in Devotion Fund's favor independent of the tax question.</p>

                    <h2>Where Modern Day Missions is genuinely the better choice</h2>

                    <p>I'd point someone to Modern Day Missions without hesitation if:</p>
                    <ul>
                        <li><strong>Your donor base is itemizing and deduction-motivated</strong> — larger gifts, higher-capacity donors, or supporters who explicitly ask for a tax receipt.</li>
                        <li><strong>You're a U.S.-based missionary</strong> operating within the American 501(c)(3) framework and comfortable with fiscal-sponsor governance.</li>
                        <li><strong>You want the accountability structure</strong> of a sponsoring organization overseeing fund usage, rather than fully direct control.</li>
                    </ul>

                    <h2>Where Devotion Fund is the better choice</h2>

                    <p>Choose us when:</p>
                    <ul>
                        <li><strong>Your donor base mostly takes the standard deduction</strong>, so the tax benefit doesn't move their giving decision.</li>
                        <li><strong>You want direct payouts</strong> to your own bank account with no fiscal-sponsor approval process or usage governance.</li>
                        <li><strong>You're serving outside the U.S. framework</strong> — international or restricted-access work that a domestic 501(c)(3) fiscal sponsor isn't built to support.</li>
                        <li><strong>Keeping supporters engaged is your real bottleneck</strong> — the built-in updates and donor CRM exist specifically to solve that.</li>
                    </ul>

                    <p>Some missionaries genuinely benefit from running both — Modern Day Missions for the itemizing donors who want a receipt, Devotion Fund for the recurring relationship and everyone else. If your support base is deduction-sensitive, don't let this post talk you out of a fiscal sponsor; I'd rather you get the structure right than pick mine by default. But if direct control and a built-in supporter relationship matter more than the deduction, <a href="/">start a page on Devotion Fund</a> — it takes about ten minutes, and I read every new signup personally.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is giving through Modern Day Missions tax-deductible?</strong><br />
                    Yes — it's a 501(c)(3) fiscal sponsor, so donors receive an official tax-deductible receipt. Devotion Fund does not offer this today.</p>

                    <p><strong>Is Modern Day Missions or Devotion Fund cheaper?</strong><br />
                    Modern Day Missions is slightly cheaper on raw fees — roughly 4.28% effective all-in versus Devotion Fund's 5% (bank transfer) or 7% (card) at a typical $50/month gift. The gap is small enough that other factors usually matter more.</p>

                    <p><strong>Can I use both?</strong><br />
                    Yes — some missionaries use Modern Day Missions for donors who need a deduction and Devotion Fund for the recurring relationship and everyone else.</p>

                    <p><strong>Who should choose Modern Day Missions over Devotion Fund?</strong><br />
                    U.S.-based missionaries with an itemizing, deduction-motivated donor base who are comfortable with 501(c)(3) fund governance.</p>

                    <hr />
                    <p><small><em>Fees verified against Modern Day Missions' published rates as cited in our full platform comparison. Devotion Fund's fee is all-in (processing absorbed): 5% on bank transfers, 7% on card. We update this post when either organization changes its terms.</em></small></p>

                    <h2>About the author</h2>
                    <p>We're the team behind Devotion Fund. We built this platform for missionaries whose support base doesn't need a tax deduction badly enough to trade away direct control of their funds and a real supporter relationship — while respecting that for other missionaries, that deduction is exactly the right call.</p>
                    <p><small><em>Last updated: 2026-07-23.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/modern-day-missions-vs-devotion-fund" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
